import { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './List.module.css';

import SimpleBar from 'simplebar-react';
import useSimpleBar from '../../../../hooks/useSimpleBar';

import { StorageItem } from '../../../../types/settings';
import { StringByKey } from '../../../../types/common';
import {
  COLLAPSE,
  DELETE,
  DELETION_SCHEDULED_FILES,
  DRIVE,
  EMPTY_TRASH,
  EXCEEDED,
  FILE,
  FILE_SIZE,
  FULL_STORAGE,
  LIBRARY,
  OPEN,
  TRASH,
} from '../../../../constants';
import { formatBytes } from '../../../../utils/util';
import useModal from '../../../../hooks/useModal';
import { TooltipWrapper } from '../../../../components/Tooltip';

interface List {
  name: string;
  list: StorageItem[];
  data: any;
}

export default function List({ name, list, data: storageData }: List) {
  const cx = classNames.bind(styles);
  const [simplebarRef] = useSimpleBar();
  const [assignableList, setAssignableList] = useState([...list]);
  const [isOpen, setIsOpen] = useState(false);
  const [isShowFileSortArrow, setIsShowFileSortArrow] = useState(false);
  const [sortOrderByFile, setSortOrderByFile] = useState('desc');
  const [sortOrderBySize, setSortOrderBySize] = useState('desc');
  const { toggleModal } = useModal();

  const { limitSize, totalSize } = storageData;
  const usedTotalPercent = Math.round((totalSize / limitSize) * 100);
  const usedSize = assignableList.reduce(
    (acc: number, resource: StorageItem) => acc + resource.size,
    0
  );
  const usagePercent = Math.round((usedSize / limitSize) * 100);
  const isExceeded = usedSize / limitSize > 1;
  const isNotEnough = usagePercent > 90;

  const storageNames: StringByKey = {
    totalList: FULL_STORAGE,
    libraryList: LIBRARY,
    archiveList: DRIVE,
    trashList: TRASH,
  };

  const openList = () => {
    setIsOpen(!isOpen);
  };

  const sortByFile = () => {
    const isDesc = sortOrderByFile === 'desc';
    setIsShowFileSortArrow(true);
    assignableList.sort((a, b) =>
      isDesc ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title)
    );
    setSortOrderByFile(isDesc ? 'asc' : 'desc');
  };

  const sortBySize = () => {
    const isDesc = sortOrderBySize === 'desc';
    assignableList.sort((a, b) => (isDesc ? a.size - b.size : b.size - a.size));
    setSortOrderBySize(isDesc ? 'asc' : 'desc');
  };

  const confirmDelete = (resourceId: number, location_ko: string) => {
    const locations: StringByKey = {
      [LIBRARY]: 'Lib',
      내서재: 'Lib',
      [DRIVE]: 'Drive',
      [TRASH]: 'Trash',
    };
    const location = locations[location_ko];

    toggleModal({
      type: 'confirm',
      id: `confirmDelete${location}Storage`,
      data: { resourceId, location },
    });
  };

  const confirmEmptyTrash = () => {
    toggleModal({
      type: 'confirm',
      id: `confirmEmptyTrashStorage`,
    });
  };

  const openFileListModal = () => {
    toggleModal({
      type: 'component',
      id: 'deletion-scheduled-files',
    });
  };

  useEffect(() => {
    setAssignableList([...list]);
  }, [list]);

  return (
    <li>
      <div className={cx('drive_items', { active: isOpen })}>
        <h3>{storageNames[name]}</h3>
        {name === 'totalList' && name === 'totalList' && isExceeded && (
          <button
            type="button"
            className={cx('btn', 'abtn')}
            onClick={openFileListModal}
          >
            <span className={cx('underline')}>{DELETION_SCHEDULED_FILES}</span>
          </button>
        )}
        <div className={cx('drive_info')}>
          <div className={cx('progress_bar')}>
            <div className={cx('progress', 'rail')}></div>
            <div
              className={cx('progress', 'total')}
              style={{
                width: `${usedTotalPercent > 100 ? 100 : usedTotalPercent}%`,
              }}
            ></div>
            <div
              className={cx('progress', 'current', name, { em: isNotEnough })}
              style={{ width: `${usagePercent > 100 ? 100 : usagePercent}%` }}
            ></div>
          </div>
          <span className={cx('drive_size')}>
            <span className={cx({ em: isNotEnough })}>
              {formatBytes(usedSize)}
            </span>{' '}
            {name === 'totalList' && (
              <>
                / {formatBytes(limitSize)}
                <span>
                  {' '}
                  (
                  <span className={cx({ em: isNotEnough })}>
                    {isExceeded ? EXCEEDED : usagePercent}
                  </span>
                  {isExceeded || '%'})
                </span>
              </>
            )}
          </span>
        </div>
        {name === 'trashList' && (
          <button
            type="button"
            className={cx('btn', 'abtn', {
              'is--disabled': !assignableList.length,
            })}
            onClick={confirmEmptyTrash}
          >
            <span className={cx('underline')}>{EMPTY_TRASH}</span>
          </button>
        )}

        <div className={cx('drive_detail')}>
          <div className={cx('table_list', 'table_head', 'drive_head')}>
            <div className={cx('items', 'option_order')}>
              <span className={cx('is_order')}>
                <span
                  className={cx('btn', 'open_order_menu')}
                  onClick={sortByFile}
                >
                  {FILE}
                </span>
                {isShowFileSortArrow && (
                  <span
                    className={cx('btn', 'order_arrow', sortOrderByFile)}
                  ></span>
                )}
              </span>
            </div>
            <div className={cx('items', 'option_order')}>
              <span className={cx('is_order')}>
                <span
                  className={cx('btn', 'open_order_menu')}
                  onClick={sortBySize}
                >
                  {FILE_SIZE}
                </span>
                <span
                  className={cx('btn', 'order_arrow', sortOrderBySize)}
                ></span>
              </span>
            </div>
            <div className={cx('items')}>
              <span className={cx('blind')}>{DELETE}</span>
            </div>
          </div>

          <SimpleBar forceVisible={true} ref={simplebarRef}>
            <div className={cx('table_list', 'table_body', 'drive_body')}>
              {assignableList.map(
                ({ resourceId, title, location, size }: StorageItem) => (
                  <div
                    key={`${location}_${resourceId}`}
                    className={cx('items')}
                  >
                    <span className={cx('sub')}>
                      <span className={cx('name', 'text_clip')}>{title}</span>
                      <span className={cx('location', 'text_clip')}>
                        {location}
                      </span>
                    </span>
                    <span className={cx('sub')}>
                      <span className={cx('size')}>{formatBytes(size)}</span>
                    </span>
                    <span className={cx('sub')}>
                      <TooltipWrapper
                        content={DELETE}
                        onClick={() => confirmDelete(resourceId, location)}
                      >
                        {(tooltipProps) => (
                          <button
                            type="button"
                            className={cx('btn', 'items_delete')}
                            {...tooltipProps}
                          >
                            {DELETE}
                          </button>
                        )}
                      </TooltipWrapper>
                    </span>
                  </div>
                )
              )}
            </div>
          </SimpleBar>
        </div>

        <TooltipWrapper content={isOpen ? COLLAPSE : OPEN} onClick={openList}>
          {(tooltipProps) => (
            <button
              type="button"
              className={cx('btn', 'open_list_more', { on: isOpen })}
              {...tooltipProps}
            >
              <i className={cx('svg_i')}></i>
              <span className={cx('blind')}>{isOpen ? COLLAPSE : OPEN}</span>
            </button>
          )}
        </TooltipWrapper>
      </div>
    </li>
  );
}
